// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Practitioner } from '@medplum/fhirtypes';

export const APPOINTMENT_TYPE_DEFAULT_STAFF_MAP = {
  'new-patient-intake': '4f6c2270-bad6-4f87-9dd7-deec39a57029',
  'follow-up': '4f6c2270-bad6-4f87-9dd7-deec39a57029',
  'check-in': 'f032e3b4-60e4-49f6-a5dc-4fe5d1132495',
} as const satisfies Record<string, string>;

export type AppointmentTypeDefaultStaffMap = typeof APPOINTMENT_TYPE_DEFAULT_STAFF_MAP;

export type ResolveDefaultStaffResult =
  | {
      status: 'resolved';
      practitioner: Practitioner;
      practitionerId: string;
    }
  | {
      status: 'invalid-config';
      appointmentTypeCode: string;
      practitionerId?: string;
      reason: 'missing-mapping' | 'not-found' | 'inactive';
    };

export function getDefaultStaffPractitionerId(appointmentTypeCode: string): string | undefined {
  return APPOINTMENT_TYPE_DEFAULT_STAFF_MAP[appointmentTypeCode as keyof AppointmentTypeDefaultStaffMap];
}

export async function resolveDefaultStaffPractitioner(
  medplum: MedplumClient,
  appointmentTypeCode: string
): Promise<ResolveDefaultStaffResult> {
  const practitionerId = getDefaultStaffPractitionerId(appointmentTypeCode);
  if (!practitionerId) {
    return {
      status: 'invalid-config',
      appointmentTypeCode,
      reason: 'missing-mapping',
    };
  }

  try {
    const practitioner = await medplum.readResource('Practitioner', practitionerId);
    if (practitioner.active === false) {
      return {
        status: 'invalid-config',
        appointmentTypeCode,
        practitionerId,
        reason: 'inactive',
      };
    }

    return {
      status: 'resolved',
      practitioner,
      practitionerId,
    };
  } catch {
    return {
      status: 'invalid-config',
      appointmentTypeCode,
      practitionerId,
      reason: 'not-found',
    };
  }
}
